/**
 * Keyboard Command Handler
 * Feature: 051-keybindings
 *
 * Global keyboard event listener that matches keypresses to commands
 * and emits them via the CommandBus.
 */

import { useEffect } from 'react';
import { useCommandBus } from './CommandBus';
import { COMMANDS } from './commandDefinitions';
import type { CommandKeybinding } from './commandDefinitions';
import logger from '../logger';

/**
 * Check if a keyboard event matches a keybinding definition
 */
function matchesKeybinding(
  event: KeyboardEvent,
  binding: CommandKeybinding
): boolean {
  // Key must match exactly
  if (event.key !== binding.key) return false;

  // Modifier keys must match
  // If binding specifies a modifier (true), it must be pressed
  // If binding doesn't specify (undefined) or specifies false, it must NOT be pressed
  const metaMatch = binding.meta ? event.metaKey : !event.metaKey;
  const ctrlMatch = binding.ctrl ? event.ctrlKey : !event.ctrlKey;
  const altMatch = binding.alt ? event.altKey : !event.altKey;
  const shiftMatch = binding.shift ? event.shiftKey : !event.shiftKey;

  return metaMatch && ctrlMatch && altMatch && shiftMatch;
}

/**
 * Global keyboard command handler component
 * Add as a child of CommandBusProvider to enable keyboard shortcuts
 *
 * @example
 * <CommandBusProvider>
 *   <KeyboardCommandHandler />
 *   <App />
 * </CommandBusProvider>
 */
export function KeyboardCommandHandler() {
  const bus = useCommandBus();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isTyping =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      // Find matching command
      for (const cmd of Object.values(COMMANDS)) {
        if (matchesKeybinding(e, cmd.keybinding)) {
          // Check if there are any handlers registered
          if (!bus.hasHandlers(cmd.id)) {
            logger.debug('KeyboardCommandHandler', 'Matched keybinding but no handlers', {
              commandId: cmd.id,
              key: e.key
            });
            continue;
          }

          // Special case: Allow Cmd/Ctrl+Enter in textareas (for submit)
          const isSubmitCommand = cmd.id === 'critique.submit';
          if (isTyping && !isSubmitCommand) {
            // Don't trigger commands while typing (except submit)
            logger.debug('KeyboardCommandHandler', 'Ignoring command while typing', {
              commandId: cmd.id,
              key: e.key
            });
            continue;
          }

          // Prevent default browser behavior
          e.preventDefault();

          // Emit command
          logger.info('KeyboardCommandHandler', 'Emitting command', {
            commandId: cmd.id,
            key: e.key,
            modifiers: {
              meta: e.metaKey,
              ctrl: e.ctrlKey,
              alt: e.altKey,
              shift: e.shiftKey
            }
          });

          bus.emit(cmd.id);

          // Stop after first match
          break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    logger.info('KeyboardCommandHandler', 'Global keyboard listener attached');

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      logger.info('KeyboardCommandHandler', 'Global keyboard listener detached');
    };
  }, [bus]);

  // No UI, just event handling
  return null;
}
